import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { RbacRole } from '../../../interfaces/rbac-role.interface';
import { RbacPermission } from '../../../interfaces/rbac-permission.interface';
import { AppService } from '../../../app.service';
import { RBACService } from '../../../shared/rbac.service';
import { RBAC_PERMISSION_DEPENDENCIES, RbacPermissionDependency } from '../../../shared/rbac-permission-dependencies';
import { getRbacFeatureDescription } from '../rbac-feature-descriptions';

interface FeatureGroup {
  rbac_permissions: RbacPermission[];
}

/**
 * A permission dependency the role does not meet: a ticked permission on a feature whose screens
 * are only reachable through a parent permission the role lacks.
 */
interface UnmetDependency {
  rule: RbacPermissionDependency;
  // The feature name (as shown in the catalogue) holding the ticked dependent permission.
  feature: string;
  // Human readable list of permissions that would satisfy the rule, e.g. "Sites: View".
  label: string;
  // The permission the one-click fix ticks.
  fixPermission: RbacPermission;
}

interface CategoryGroup {
  name: string;
  icon: string;
  expanded: boolean;
  features: { [key: string]: FeatureGroup };
}

@Component({
  selector: 'app-rbac-roles-edit',
  templateUrl: './rbac-roles-edit.component.html',
  styleUrls: ['./rbac-roles-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RbacRolesEditComponent implements OnInit {

  // Reference to the form for external save triggering.
  @ViewChild('f') form: NgForm;

  // Get the RBAC role id.
  @Input('rbac_role_id') rbac_role_id: number;

  // Define the RBAC role id changed event emitter.
  @Output('rbac_role_id_changed') rbac_role_id_changed: EventEmitter<number> = new EventEmitter<number>();

  // Define the "close dialog" event emitter.
  @Output('close_dialog') close_dialog: EventEmitter<boolean> = new EventEmitter<boolean>();

  // The RBAC role interface.
  rbac_role: RbacRole = {
    id: 0,
    name: '',
    rbac_permissions: []
  } as RbacRole;

  // All RBAC permissions.
  rbac_permissions: RbacPermission[] = [];

  // Grouped RBAC permissions, keyed by feature.
  grouped_rbac_permissions: { [key: string]: {
    rbac_permissions: RbacPermission[]
  } } = {};

  // Filtered grouped permissions for search functionality.
  filtered_grouped_rbac_permissions: { [key: string]: {
    rbac_permissions: RbacPermission[]
  } } = {};

  // Category-based grouping for accordion layout.
  categories: { [key: string]: CategoryGroup } = {};

  // Filtered categories for search functionality.
  filtered_categories: { [key: string]: CategoryGroup } = {};

  // Dynamically built category configuration from permissions data.
  categoryConfig: { [key: string]: { icon: string; order: number } } = {};

  // Icon mapping for common category name patterns.
  private categoryIconMap: { [key: string]: string } = {
    'account': 'business',
    'site': 'location_on',
    'contractor': 'engineering',
    'employee': 'people',
    'safety': 'health_and_safety',
    'register': 'list_alt',
    'template': 'description',
    'report': 'assessment',
    'task': 'task_alt',
    'form': 'dynamic_form',
    'message': 'mail',
    'client': 'handshake',
    'hazard': 'warning',
    'incident': 'report_problem',
    'audit': 'fact_check',
    'inspection': 'search',
    'document': 'folder',
    'tool': 'build',
    'training': 'school',
    'visitor': 'person_outline'
  };

  // Search text for filtering features.
  searchText: string = '';

  // All unique actions across all permissions
  availableActions: string[] = [];

  // Catalogue permissions keyed by permission string (e.g. 'sites:view'). Built after the catalogue
  // is plan-filtered and grouped. The objects are shared with rbac_permissions, so their checked
  // state is always current.
  private permissionIndex: Map<string, RbacPermission> = new Map<string, RbacPermission>();

  constructor(
    public utils: UtilsService,
    private api: ApiRequestService,
    public app: AppService,
    private rbac: RBACService
  ) { }

  ngOnInit(): void {
    // Get all RBAC permissions.
    this.getRBACPermissions().then((): void => {
      // Get the RBAC role by id.
      if ( this.rbac_role_id ) {
        this.getRBACRoleById(this.rbac_role_id);
      } else {
        // Group the RBAC permissions by feature.
        this.groupRBACPermissionsByFeature();
      }
    });
  }

  /**
   * Retrieves the RBAC role by ID.
   *
   * @param {number} rbac_role_id - The ID of the RBAC role to retrieve.
   * @returns {undefined}
   */
  getRBACRoleById(rbac_role_id: number): void {
    // Make an API request to get the RBAC role if it exists.
    this.api.makeRequest('get', `v2/account/rbac-roles/${rbac_role_id}`)
      .then((response: any): void => {
        // Apply the RBAC role from the API.
        this.rbac_role = response;
        // Map the RBAC permissions.
        this.mapRBACPermissions();
      })
      .catch((errorResponse: any): void => {
        // Handle API error responses.
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Maps the RBAC permissions by marking them as checked if they exist in the RBAC role's permissions.
   * This method updates the 'checked' property of each permission in the 'rbac_permissions' array.
   *
   * @returns {void} - This method does not return anything.
   */
  mapRBACPermissions(): void {
    this.rbac_permissions = this.rbac_permissions.map((rbac_permission: RbacPermission): RbacPermission => {
      rbac_permission.checked = this.rbac_role.rbac_permissions.some((role_rbac_permission: RbacPermission): boolean => {
        return role_rbac_permission.id == rbac_permission.id;
      });
      return rbac_permission;
    });
    // Group the RBAC permissions by feature.
    this.groupRBACPermissionsByFeature();
  }

  /**
   * Retrieves the RBAC permission catalogue from the API.
   *
   * @returns {Promise<any>} - A promise that resolves with the RBAC permissions data.
   */
  async getRBACPermissions(): Promise<any> {
    // Make an API request to get all RBAC permissions.
    return this.api.makeRequest('get', `v2/rbac-permissions`)
      .then((response: any): void => {
        // Apply the RBAC permissions from the API.
        this.rbac_permissions = response.map((rbac_permission: RbacPermission) => {
          return rbac_permission as RbacPermission;
        });
        // Plan-aware filtering (Layer 1). Reduce the catalogue to only the permissions the active
        // account's tier unlocks BEFORE any grouping, so grouping, counts, bulk actions and search
        // all operate on the filtered set, and empty features/categories are never created.
        this.applyPlanFilter();
      })
      .catch((errorResponse: any): void => {
        // Handle API error responses.
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Reduces the fetched catalogue to only the permissions whose RBAC feature is unlocked by the
   * active account's subscription tier. The RBAC feature slug is the segment of the permission
   * string before the colon (e.g. 'sites-incidents' in 'sites-incidents:view'). Premium accounts
   * pass every check, so the filter is a no op for them. Runs before grouping.
   *
   * Gated on the account's subscription, NOT on what the signed-in user may use, so the super admin
   * bypass in planCan() does not apply. A role belongs to the account, so its contents must follow
   * that account's plan whoever is editing it. Using the requester-scoped check previously offered a
   * super admin the entire catalogue on a Free account, and the API then stored it, while the same
   * account's plan-derived Administrators role showed only the free subset.
   *
   * @returns {void}
   */
  private applyPlanFilter(): void {
    this.rbac_permissions = this.rbac_permissions.filter((p: RbacPermission): boolean => {
      const slug: string = (p.permission || '').split(':')[0];
      return this.app.subscriptionCanRbacFeature(slug);
    });
  }

  /**
   * Groups the RBAC permissions by feature.
   *
   * @returns {void}
   */
  groupRBACPermissionsByFeature(): void {
    // Remember which categories are expanded so a rebuild (for example after saving) keeps the view as it was.
    const previousExpandedState: { [key: string]: boolean } = {};
    Object.keys(this.filtered_categories).forEach((key: string): void => {
      previousExpandedState[key] = this.filtered_categories[key].expanded;
    });

    // Transform the RBAC permissions into groups keyed by feature.
    this.grouped_rbac_permissions = this.rbac_permissions.reduce((grouped, rbac_permission: RbacPermission) => {
      // Get the feature.
      const feature: string = rbac_permission.feature;
      // Check if the grouping exists.
      if ( !grouped[feature] ) {
        grouped[feature] = {
          rbac_permissions: []
        };
      }
      // Push the permission into the group.
      grouped[feature].rbac_permissions.push(rbac_permission);
      // Return the grouped permissions.
      return grouped;
    }, {});

    // Initialize filtered permissions with all permissions.
    this.filtered_grouped_rbac_permissions = { ...this.grouped_rbac_permissions };

    // Build category-based grouping.
    this.buildCategoryGroups();

    // Calculate all available actions across all permissions
    this.calculateAvailableActions();

    // Index the catalogue by permission string for the dependency checks.
    this.permissionIndex = new Map<string, RbacPermission>();
    this.rbac_permissions.forEach((p: RbacPermission): void => {
      this.permissionIndex.set(p.permission, p);
    });

    // The rebuild above resets the filtered views to the full, collapsed list. Re-apply the active search
    // text so the results still match what the search field shows.
    if ( this.searchText && this.searchText.trim() !== '' ) {
      this.onFilterFeatures();
    }

    // Restore the expanded or collapsed state of the categories that were visible before the rebuild.
    Object.keys(this.filtered_categories).forEach((key: string): void => {
      if ( key in previousExpandedState ) {
        this.filtered_categories[key].expanded = previousExpandedState[key];
      }
    });
  }

  /**
   * Returns the dependency rules a feature row does not meet. A rule is unmet when the row has a
   * ticked permission the rule applies to and none of the rule's required permissions is ticked.
   * Rules whose required permissions are not in the (plan-filtered) catalogue are skipped, because
   * they could not be fixed from here. Default roles are system managed, so they are never flagged.
   *
   * @param {RbacPermission[]} featurePermissions - All permissions of one feature row.
   * @returns {UnmetDependency[]}
   */
  getUnmetDependencies(featurePermissions: RbacPermission[]): UnmetDependency[] {
    if ( this.isReadOnly || !featurePermissions || featurePermissions.length === 0 ) {
      return [];
    }

    const unmet: UnmetDependency[] = [];
    const slug: string = this.getFeatureSlug(featurePermissions[0]);

    RBAC_PERMISSION_DEPENDENCIES.forEach((rule: RbacPermissionDependency): void => {
      if ( !rule.features.includes(slug) ) {
        return;
      }

      // Does the row tick a permission this rule applies to?
      const triggered: boolean = featurePermissions.some((p: RbacPermission): boolean => {
        return p.checked && (!rule.actions || rule.actions.includes(p.action));
      });
      if ( !triggered ) {
        return;
      }

      // The required permissions that exist in this account's catalogue.
      const available: RbacPermission[] = rule.requiresAnyOf
        .map((permission: string): RbacPermission => this.permissionIndex.get(permission))
        .filter((p: RbacPermission): boolean => !!p);
      if ( available.length === 0 || available.some((p: RbacPermission): boolean => p.checked) ) {
        return;
      }

      unmet.push({
        rule: rule,
        feature: featurePermissions[0].feature,
        label: available.map((p: RbacPermission): string => this.getPermissionLabel(p)).join(' or '),
        fixPermission: available[0]
      });
    });

    return unmet;
  }

  /**
   * Returns every unmet dependency across the whole catalogue.
   *
   * @returns {UnmetDependency[]}
   */
  getAllUnmetDependencies(): UnmetDependency[] {
    return Object.values(this.grouped_rbac_permissions).reduce((acc: UnmetDependency[], group): UnmetDependency[] => {
      return acc.concat(this.getUnmetDependencies(group.rbac_permissions));
    }, []);
  }

  /**
   * Counts the feature rows in a category that have an unmet dependency.
   *
   * @param {string} categoryKey - The category key.
   * @returns {number}
   */
  getCategoryUnmetCount(categoryKey: string): number {
    const category: CategoryGroup = this.categories[categoryKey];
    if ( !category ) {
      return 0;
    }
    return Object.values(category.features).filter((feature: FeatureGroup): boolean => {
      return this.getUnmetDependencies(feature.rbac_permissions).length > 0;
    }).length;
  }

  /**
   * Returns the dependency hints that apply to a category: one per rule that covers at least one
   * feature in the category, and whose required permission exists in the catalogue.
   *
   * @param {string} categoryKey - The category key.
   * @returns {string[]}
   */
  getCategoryDependencyHints(categoryKey: string): string[] {
    const category: CategoryGroup = this.categories[categoryKey];
    if ( !category || this.isReadOnly ) {
      return [];
    }

    const slugs: string[] = Object.values(category.features)
      .filter((feature: FeatureGroup): boolean => feature.rbac_permissions.length > 0)
      .map((feature: FeatureGroup): string => this.getFeatureSlug(feature.rbac_permissions[0]));

    const hints: string[] = [];
    RBAC_PERMISSION_DEPENDENCIES.forEach((rule: RbacPermissionDependency): void => {
      const applies: boolean = slugs.some((slug: string): boolean => rule.features.includes(slug));
      const fixable: boolean = rule.requiresAnyOf.some((permission: string): boolean => this.permissionIndex.has(permission));
      if ( applies && fixable && !hints.includes(rule.hint) ) {
        hints.push(rule.hint);
      }
    });
    return hints;
  }

  /**
   * Fixes one unmet dependency by ticking the required permission. The usual same-feature rule then
   * applies, so ticking an edit permission also ticks that feature's view permission.
   *
   * @param {UnmetDependency} unmet - The dependency to fix.
   */
  onFixDependency(unmet: UnmetDependency): void {
    if ( this.isReadOnly ) {
      return;
    }
    const permission: RbacPermission = unmet.fixPermission;
    permission.checked = true;
    const group = this.grouped_rbac_permissions[permission.feature];
    this.onPermissionChange(permission, group ? group.rbac_permissions : [permission]);
  }

  /**
   * Fixes every unmet dependency. Repeats until none remain, because one fix can satisfy several
   * rows (and could in principle expose another rule).
   */
  onFixAllDependencies(): void {
    let unmet: UnmetDependency[] = this.getAllUnmetDependencies();
    let guard: number = 0;
    while ( unmet.length > 0 && guard < 10 ) {
      unmet.forEach((u: UnmetDependency): void => this.onFixDependency(u));
      unmet = this.getAllUnmetDependencies();
      guard++;
    }
  }

  /**
   * trackBy for the per-row dependency chips. getUnmetDependencies() returns new objects on every
   * change detection, and hovering or pressing the mouse triggers one. Without a stable key, ngFor
   * would destroy and recreate the button between mousedown and mouseup, so its click never fired.
   *
   * @param {number} index - The item index.
   * @param {UnmetDependency} unmet - The unmet dependency.
   * @returns {string}
   */
  trackUnmetDependency(index: number, unmet: UnmetDependency): string {
    return unmet.feature + '|' + unmet.rule.requiresAnyOf.join(',') + '|' + (unmet.rule.actions || []).join(',');
  }

  /**
   * The tooltip for an unmet dependency chip.
   *
   * @param {UnmetDependency} unmet - The unmet dependency.
   * @returns {string}
   */
  getDependencyTooltip(unmet: UnmetDependency): string {
    return `${unmet.rule.reason} Without it these permissions have no effect in the Web Portal. Click to add ${unmet.label}.`;
  }

  /**
   * The feature slug of a permission, e.g. 'sites-documents' for 'sites-documents:view'.
   *
   * @param {RbacPermission} permission - The permission.
   * @returns {string}
   */
  private getFeatureSlug(permission: RbacPermission): string {
    return (permission.permission || '').split(':')[0];
  }

  /**
   * A readable label for a permission, e.g. "Sites: View" or "Contractors: Edit".
   *
   * @param {RbacPermission} permission - The permission.
   * @returns {string}
   */
  private getPermissionLabel(permission: RbacPermission): string {
    const action: string = permission.action.charAt(0).toUpperCase() + permission.action.slice(1);
    return `${permission.feature}: ${action}`;
  }

  /**
   * Builds the list items for the save confirmation. Groups the unmet dependencies by the
   * permission they need, so the message has one line per missing permission rather than one per
   * feature, and names at most a handful of features per line to keep it short on small screens.
   *
   * @param {UnmetDependency[]} unmet - The unmet dependencies.
   * @returns {string} HTML list items.
   */
  private buildUnmetDependencySummary(unmet: UnmetDependency[]): string {
    const maxNames: number = 4;
    const groups: Map<string, string[]> = new Map<string, string[]>();

    unmet.forEach((u: UnmetDependency): void => {
      // Drop the category prefix ("Sites: Documents" becomes "Documents") to keep each line short.
      const category: string = this.extractCategoryFromFeature(u.feature);
      const name: string = this.getDisplayFeatureName(u.feature, category);
      const names: string[] = groups.get(u.label) || [];
      if ( !names.includes(name) ) {
        names.push(name);
      }
      groups.set(u.label, names);
    });

    return Array.from(groups.entries()).map(([label, names]: [string, string[]]): string => {
      const shown: string = names.slice(0, maxNames).map((n: string): string => this.escapeHtml(n)).join(', ');
      const more: number = names.length - maxNames;
      const list: string = more > 0 ? `${shown} and ${more} more` : shown;
      const count: string = names.length === 1 ? '1 feature' : `${names.length} features`;
      return `<li><b>${this.escapeHtml(label)}</b> is needed by ${count}: ${list}.</li>`;
    }).join('');
  }

  /**
   * Escapes text for the HTML body of the shared confirmation modal.
   *
   * @param {string} text - The text to escape.
   * @returns {string}
   */
  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /**
   * Builds category-based grouping from the feature groups.
   *
   * @returns {void}
   */
  buildCategoryGroups(): void {
    this.categories = {};

    // First pass: extract all unique categories and build config dynamically.
    const uniqueCategories = new Set<string>();
    Object.keys(this.grouped_rbac_permissions).forEach((featureName: string): void => {
      const categoryName = this.extractCategoryFromFeature(featureName);
      uniqueCategories.add(categoryName);
    });

    // Build categoryConfig dynamically from extracted categories.
    this.categoryConfig = {};
    const sortedCategories = Array.from(uniqueCategories).sort((a, b) => a.localeCompare(b));
    sortedCategories.forEach((categoryName: string, index: number): void => {
      this.categoryConfig[categoryName] = {
        icon: this.getIconForCategory(categoryName),
        order: index + 1
      };
    });

    // Second pass: build the category groups.
    Object.keys(this.grouped_rbac_permissions).forEach((featureName: string): void => {
      const categoryName = this.extractCategoryFromFeature(featureName);
      const displayFeatureName = this.getDisplayFeatureName(featureName, categoryName);

      if (!this.categories[categoryName]) {
        const config = this.categoryConfig[categoryName];
        this.categories[categoryName] = {
          name: categoryName,
          icon: config.icon,
          expanded: false,
          features: {}
        };
      }

      this.categories[categoryName].features[displayFeatureName] = this.grouped_rbac_permissions[featureName];
    });

    // Initialize filtered categories.
    this.filtered_categories = this.deepCopyCategories(this.categories);
  }

  /**
   * Gets an appropriate icon for a category based on its name.
   *
   * @param {string} categoryName - The category name
   * @returns {string} - The Material icon name
   */
  getIconForCategory(categoryName: string): string {
    const lowerName = categoryName.toLowerCase();

    // Check for exact or partial matches in the icon map.
    for (const [key, icon] of Object.entries(this.categoryIconMap)) {
      if (lowerName.includes(key) || key.includes(lowerName)) {
        return icon;
      }
    }

    // Default icon for unknown categories.
    return 'folder_open';
  }

  /**
   * Extracts the category name from a feature name.
   *
   * @param {string} featureName - The full feature name (e.g., "Account: Settings")
   * @returns {string} - The category name (e.g., "Account")
   */
  extractCategoryFromFeature(featureName: string): string {
    if (featureName.includes(':')) {
      return featureName.split(':')[0].trim();
    }
    // Single-word features become their own category.
    return featureName;
  }

  /**
   * Gets the display name for a feature within a category.
   *
   * @param {string} featureName - The full feature name
   * @param {string} categoryName - The category name
   * @returns {string} - The display name without category prefix
   */
  getDisplayFeatureName(featureName: string, categoryName: string): string {
    if (featureName.includes(':')) {
      return featureName.split(':')[1].trim();
    }
    return featureName;
  }

  /**
   * Deep copies the categories object.
   *
   * @param {object} categories - The categories object to copy
   * @returns {object} - A deep copy of the categories
   */
  deepCopyCategories(categories: { [key: string]: CategoryGroup }): { [key: string]: CategoryGroup } {
    const copy: { [key: string]: CategoryGroup } = {};
    Object.keys(categories).forEach((key: string): void => {
      copy[key] = {
        ...categories[key],
        features: { ...categories[key].features }
      };
    });
    return copy;
  }

  /**
   * Gets sorted category keys based on the defined order.
   *
   * @returns {string[]} - Sorted array of category keys
   */
  getSortedCategoryKeys(): string[] {
    return Object.keys(this.filtered_categories).sort((a, b) => {
      const orderA = this.categoryConfig[a]?.order || 99;
      const orderB = this.categoryConfig[b]?.order || 99;
      return orderA - orderB;
    });
  }

  /**
   * Toggles the expanded state of a category.
   *
   * @param {string} categoryKey - The category key to toggle
   */
  toggleCategory(categoryKey: string): void {
    if (this.filtered_categories[categoryKey]) {
      this.filtered_categories[categoryKey].expanded = !this.filtered_categories[categoryKey].expanded;
    }
  }

  /**
   * Checks if all categories are currently expanded.
   *
   * @returns {boolean}
   */
  areAllCategoriesExpanded(): boolean {
    const keys = Object.keys(this.filtered_categories);
    if (keys.length === 0) { return false; }
    return keys.every((key: string) => this.filtered_categories[key].expanded);
  }

  /**
   * Toggles all categories between expanded and collapsed.
   */
  toggleAllCategories(): void {
    const shouldExpand = !this.areAllCategoriesExpanded();
    Object.keys(this.filtered_categories).forEach((key: string): void => {
      this.filtered_categories[key].expanded = shouldExpand;
    });
  }

  /**
   * Expands all categories.
   */
  expandAllCategories(): void {
    Object.keys(this.filtered_categories).forEach((key: string): void => {
      this.filtered_categories[key].expanded = true;
    });
  }

  /**
   * Collapses all categories.
   */
  collapseAllCategories(): void {
    Object.keys(this.filtered_categories).forEach((key: string): void => {
      this.filtered_categories[key].expanded = false;
    });
  }

  /**
   * Gets the total number of permissions.
   *
   * @returns {number}
   */
  getTotalPermissionsCount(): number {
    return this.rbac_permissions.length;
  }

  /**
   * Gets the number of enabled permissions.
   *
   * @returns {number}
   */
  getEnabledPermissionsCount(): number {
    return this.rbac_permissions.filter((p: RbacPermission) => p.checked).length;
  }

  /**
   * Gets the percentage of enabled permissions.
   *
   * @returns {number}
   */
  getEnabledPercentage(): number {
    const total = this.getTotalPermissionsCount();
    if (total === 0) { return 0; }
    return Math.round((this.getEnabledPermissionsCount() / total) * 100);
  }

  /**
   * Gets the count of enabled permissions for a specific category.
   *
   * @param {string} categoryKey - The category key
   * @returns {{ enabled: number; total: number }}
   */
  getCategoryPermissionCount(categoryKey: string): { enabled: number; total: number } {
    const category = this.categories[categoryKey];
    if (!category) { return { enabled: 0, total: 0 }; }

    let enabled = 0;
    let total = 0;

    Object.values(category.features).forEach((feature: FeatureGroup): void => {
      feature.rbac_permissions.forEach((p: RbacPermission): void => {
        total++;
        if (p.checked) { enabled++; }
      });
    });

    return { enabled, total };
  }

  /**
   * Gets the count of enabled permissions for a specific feature.
   *
   * @param {RbacPermission[]} permissions - The feature permissions
   * @returns {{ enabled: number; total: number }}
   */
  getFeaturePermissionCount(permissions: RbacPermission[]): { enabled: number; total: number } {
    const total = permissions.length;
    const enabled = permissions.filter((p: RbacPermission) => p.checked).length;
    return { enabled, total };
  }

  /**
   * Returns the tooltip text describing what a feature covers, or null when none is defined
   * (the template then shows no info icon). Looked up by the feature slug, not the display label.
   *
   * @param {RbacPermission[]} featurePermissions - All permissions of one feature row.
   * @returns {string | null}
   */
  getFeatureDescription(featurePermissions: RbacPermission[]): string | null {
    if ( !featurePermissions || featurePermissions.length === 0 ) {
      return null;
    }
    return getRbacFeatureDescription(featurePermissions[0].permission);
  }

  /**
   * Sets full access (all permissions) for all features.
   */
  setAllFullAccess(): void {
    this.rbac_permissions.forEach((p: RbacPermission): void => {
      p.checked = true;
    });
  }

  /**
   * Sets view-only access for all features.
   */
  setAllViewOnly(): void {
    this.rbac_permissions.forEach((p: RbacPermission): void => {
      p.checked = p.action === 'view';
    });
  }

  /**
   * Sets no access for all features.
   */
  setAllNoAccess(): void {
    this.rbac_permissions.forEach((p: RbacPermission): void => {
      p.checked = false;
    });
  }

  /**
   * Sets full access (all permissions) for a feature.
   *
   * @param {RbacPermission[]} permissions - The feature permissions
   */
  setFeatureFullAccess(permissions: RbacPermission[]): void {
    permissions.forEach((p: RbacPermission): void => {
      p.checked = true;
    });
  }

  /**
   * Sets view-only access for a feature.
   *
   * @param {RbacPermission[]} permissions - The feature permissions
   */
  setFeatureViewOnly(permissions: RbacPermission[]): void {
    permissions.forEach((p: RbacPermission): void => {
      p.checked = p.action === 'view';
    });
  }

  /**
   * Sets no access for a feature.
   *
   * @param {RbacPermission[]} permissions - The feature permissions
   */
  setFeatureNoAccess(permissions: RbacPermission[]): void {
    permissions.forEach((p: RbacPermission): void => {
      p.checked = false;
    });
  }

  /**
   * Sets full access for an entire category.
   *
   * @param {string} categoryKey - The category key
   */
  setCategoryFullAccess(categoryKey: string): void {
    const category = this.categories[categoryKey];
    if (!category) { return; }

    Object.values(category.features).forEach((feature: FeatureGroup): void => {
      this.setFeatureFullAccess(feature.rbac_permissions);
    });
  }

  /**
   * Sets view-only access for an entire category.
   *
   * @param {string} categoryKey - The category key
   */
  setCategoryViewOnly(categoryKey: string): void {
    const category = this.categories[categoryKey];
    if (!category) { return; }

    Object.values(category.features).forEach((feature: FeatureGroup): void => {
      this.setFeatureViewOnly(feature.rbac_permissions);
    });
  }

  /**
   * Sets no access for an entire category.
   *
   * @param {string} categoryKey - The category key
   */
  setCategoryNoAccess(categoryKey: string): void {
    const category = this.categories[categoryKey];
    if (!category) { return; }

    Object.values(category.features).forEach((feature: FeatureGroup): void => {
      this.setFeatureNoAccess(feature.rbac_permissions);
    });
  }

  /**
   * Checks if a feature has full access.
   *
   * @param {RbacPermission[]} permissions - The feature permissions
   * @returns {boolean}
   */
  hasFeatureFullAccess(permissions: RbacPermission[]): boolean {
    return permissions.length > 0 && permissions.every((p: RbacPermission) => p.checked);
  }

  /**
   * Checks if a feature has view-only access.
   *
   * @param {RbacPermission[]} permissions - The feature permissions
   * @returns {boolean}
   */
  hasFeatureViewOnly(permissions: RbacPermission[]): boolean {
    const viewPermission = permissions.find((p: RbacPermission) => p.action === 'view');
    const otherPermissions = permissions.filter((p: RbacPermission) => p.action !== 'view');
    return viewPermission?.checked === true && otherPermissions.every((p: RbacPermission) => !p.checked);
  }

  /**
   * Checks if a feature has no access.
   *
   * @param {RbacPermission[]} permissions - The feature permissions
   * @returns {boolean}
   */
  hasFeatureNoAccess(permissions: RbacPermission[]): boolean {
    return permissions.every((p: RbacPermission) => !p.checked);
  }

  /**
   * Gets the original feature name from category and display name.
   *
   * @param {string} categoryKey - The category key
   * @param {string} displayName - The display feature name
   * @returns {string} - The original feature name
   */
  getOriginalFeatureName(categoryKey: string, displayName: string): string {
    // Check if it's a standalone feature (category equals display name).
    if (categoryKey === displayName) {
      return displayName;
    }
    return `${categoryKey}: ${displayName}`;
  }

  /**
   * Calculates all unique actions that exist across all permissions.
   *
   * @returns {void}
   */
  calculateAvailableActions(): void {
    const actionsSet = new Set<string>();
    this.rbac_permissions.forEach((permission: RbacPermission): void => {
      actionsSet.add(permission.action);
    });

    const sortOrder = ['create', 'view', 'edit', 'delete'];

    this.availableActions = Array.from(actionsSet).sort((a, b) => {
      const indexA = sortOrder.indexOf(a);
      const indexB = sortOrder.indexOf(b);

      if (indexA > -1 && indexB > -1) {
        return indexA - indexB;
      }

      if (indexA > -1) {
        return -1;
      }

      if (indexB > -1) {
        return 1;
      }

      return a.localeCompare(b);
    });
  }

  /**
   * Gets the Material icon name for a given action.
   *
   * @param {string} action - The action type (e.g., 'view', 'edit', 'delete')
   * @returns {string} - The Material icon name
   */
  getIconForAction(action: string): string {
    const iconMap: { [key: string]: string } = {
      'view': 'visibility',
      'edit': 'edit',
      'delete': 'delete',
      'create': 'add',
      'export': 'download'
    };
    return iconMap[action.toLowerCase()] || 'check_circle';
  }

  /**
   * Gets the permission for a specific feature and action.
   *
   * @param {RbacPermission[]} featurePermissions - Array of permissions for a feature
   * @param {string} action - The action to find
   * @returns {RbacPermission | undefined} - The permission if found, undefined otherwise
   */
  getPermissionForAction(featurePermissions: RbacPermission[], action: string): RbacPermission | undefined {
    return featurePermissions.find((p: RbacPermission) => p.action === action);
  }

  /**
   * Filters features based on the search text.
   *
   * @returns {void}
   */
  onFilterFeatures(): void {
    if (!this.searchText || this.searchText.trim() === '') {
      // If the search is empty, show all features.
      this.filtered_grouped_rbac_permissions = { ...this.grouped_rbac_permissions };
      this.filtered_categories = this.deepCopyCategories(this.categories);
      // Expand all categories when search is cleared.
      this.expandAllCategories();
      return;
    }

    const searchLower: string = this.searchText.toLowerCase().trim();
    const filtered: { [key: string]: { rbac_permissions: RbacPermission[] } } = {};

    // Filter features by name.
    Object.keys(this.grouped_rbac_permissions).forEach((featureName: string): void => {
      if (featureName.toLowerCase().includes(searchLower)) {
        filtered[featureName] = this.grouped_rbac_permissions[featureName];
      }
    });

    this.filtered_grouped_rbac_permissions = filtered;

    // Filter categories based on search.
    this.filtered_categories = {};
    Object.keys(this.categories).forEach((categoryKey: string): void => {
      const category = this.categories[categoryKey];
      const filteredFeatures: { [key: string]: FeatureGroup } = {};

      // Check if category name matches.
      const categoryMatches = categoryKey.toLowerCase().includes(searchLower);

      Object.keys(category.features).forEach((featureName: string): void => {
        const originalName = this.getOriginalFeatureName(categoryKey, featureName);
        if (categoryMatches || originalName.toLowerCase().includes(searchLower) || featureName.toLowerCase().includes(searchLower)) {
          filteredFeatures[featureName] = category.features[featureName];
        }
      });

      if (Object.keys(filteredFeatures).length > 0) {
        this.filtered_categories[categoryKey] = {
          ...category,
          features: filteredFeatures,
          expanded: true // Auto-expand when searching.
        };
      }
    });
  }

  /**
   * Clears the search text and resets the filter.
   *
   * @returns {void}
   */
  clearSearch(): void {
    this.searchText = '';
    this.onFilterFeatures();
  }

  /**
   * Toggles all permissions of a specific type (view, edit, or delete) for all features.
   *
   * @param {string} permissionType - The type of permission ('view', 'edit', or 'delete')
   * @param {boolean} checked - Whether to check or uncheck all permissions
   * @returns {void}
   */
  toggleAll(permissionType: string, checked: boolean): void {
    Object.values(this.grouped_rbac_permissions).forEach((group) => {
      group.rbac_permissions.forEach((rbac_permission: RbacPermission) => {
        if (rbac_permission.action.includes(permissionType)) {
          rbac_permission.checked = checked;

          // If enabling edit or delete, also enable view for this feature
          if (checked && (permissionType === 'edit' || permissionType === 'delete')) {
            this.enableViewForFeature(group.rbac_permissions);
          }

          // If disabling the view, also disable edit and delete for this feature
          if (!checked && permissionType === 'view') {
            this.disableEditDeleteForFeature(group.rbac_permissions);
          }
        }
      });
    });
  }

  /**
   * Enables the view permission for a specific feature.
   *
   * @param {RbacPermission[]} featurePermissions - Array of permissions for a feature
   * @returns {void}
   */
  private enableViewForFeature(featurePermissions: RbacPermission[]): void {
    const viewPermission = featurePermissions.find((p: RbacPermission) => {
      return p.action.includes('view');
    });

    if (viewPermission) {
      viewPermission.checked = true;
    }
  }

  /**
   * Disables the edit and delete permissions for a specific feature.
   *
   * @param {RbacPermission[]} featurePermissions - Array of permissions for a feature
   * @returns {void}
   */
  private disableEditDeleteForFeature(featurePermissions: RbacPermission[]): void {
    featurePermissions.forEach((p: RbacPermission) => {
      if (p.action.includes('edit') || p.action.includes('delete')) {
        p.checked = false;
      }
    });
  }

  /**
   * Handles permission checkbox change and auto-enables view when edit/delete is enabled.
   * Also disables edit/delete when view is disabled.
   *
   * @param {RbacPermission} permission - The permission that was changed
   * @param {RbacPermission[]} featurePermissions - All permissions for the feature
   * @returns {void}
   */
  onPermissionChange(permission: RbacPermission, featurePermissions: RbacPermission[]): void {
    // If enabling edit or delete, also enable view for this feature
    if (permission.checked && (permission.action.includes('edit') || permission.action.includes('delete'))) {
      this.enableViewForFeature(featurePermissions);
    }

    // If disabling the view, also disable edit and delete for this feature
    if (!permission.checked && permission.action.includes('view')) {
      this.disableEditDeleteForFeature(featurePermissions);
    }
  }

  /**
   * Checks if all permissions of a specific type are checked.
   *
   * @param {string} permissionType - The type of permission ('view', 'edit', or 'delete')
   * @returns {boolean} - True if all permissions of this type are checked
   */
  isAllChecked(permissionType: string): boolean {
    const allPermissions = this.getAllPermissionsByType(permissionType);
    return allPermissions.length > 0 && allPermissions.every(p => p.checked);
  }

  /**
   * Checks if some (but not all) permissions of a specific type are checked.
   *
   * @param {string} permissionType - The type of permission ('view', 'edit', or 'delete')
   * @returns {boolean} - True if some permissions of this type are checked
   */
  isSomeChecked(permissionType: string): boolean {
    const allPermissions = this.getAllPermissionsByType(permissionType);
    const checkedCount = allPermissions.filter(p => p.checked).length;
    return checkedCount > 0 && checkedCount < allPermissions.length;
  }

  /**
   * Gets all permissions of a specific type from all features.
   *
   * @param {string} permissionType - The type of permission ('view', 'edit', or 'delete')
   * @returns {RbacPermission[]} - Array of permissions matching the type
   */
  private getAllPermissionsByType(permissionType: string): RbacPermission[] {
    const permissions: RbacPermission[] = [];
    Object.values(this.grouped_rbac_permissions).forEach((group) => {
      group.rbac_permissions.forEach((rbac_permission: RbacPermission) => {
        if (rbac_permission.action.includes(permissionType)) {
          permissions.push(rbac_permission);
        }
      });
    });
    return permissions;
  }

  /**
   * Whether the form is currently valid. Used by the parent component to enable/disable save buttons.
   */
  get isFormValid(): boolean {
    return this.form?.valid ?? false;
  }

  /**
   * Whether the loaded role is a system-managed default role (Administrators / Users). Default roles
   * cannot be renamed or have their permissions changed; only their employees can be managed. The
   * is_default flag is stored as 0/1, so it is coerced to a boolean.
   */
  get isDefault(): boolean {
    return !!this.rbac_role?.is_default;
  }

  /**
   * Whether the loaded role is a partner role (managed by the partner sync from the partner's
   * permission template).
   */
  get isPartnerRole(): boolean {
    return this.rbac_role?.partner_user_id != null;
  }

  /**
   * Whether the role's name and permissions are read-only: default roles for everyone, partner roles
   * for everyone except super admins.
   */
  get isReadOnly(): boolean {
    return this.isDefault || this.rbac.partnerLockApplies(this.isPartnerRole);
  }

  /**
   * Whether the partner lock blocks saving this role (shows the managed-by message).
   */
  private refusePartnerRoleSave(): boolean {
    if ( this.isPartnerRole && this.rbac.partnerLockApplies(true) ) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return true;
    }
    return false;
  }

  /**
   * Public method called by the parent component to trigger a save.
   * @param autoCloseDialog Whether to close the dialog after saving.
   */
  onSave(autoCloseDialog: boolean): void {
    this.onSubmit(this.form, autoCloseDialog);
  }

  /**
   * Validate the form input and save the data.
   * @param form The NgForm object that is used for validation.
   * @param autoCloseDialog An optional param to indicate if the dialog should be closed or not.
   * @returns Nothing.
   */
  onSubmit(form: NgForm, autoCloseDialog?: boolean): void {
    // Partner roles are read-only to clients (defensive: the save buttons are hidden).
    if ( this.refusePartnerRoleSave() ) {
      return;
    }

    // Check if the submitted form is valid or not.
    if ( !form.valid ) {
      this.utils.showFormValidationError('Please enter all required fields.');
      return;
    }

    // Warn (but do not block) when ticked permissions are missing the parent permission they need.
    // A half-built role is a legitimate state, so the user may save anyway.
    const unmet: UnmetDependency[] = this.getAllUnmetDependencies();
    if ( unmet.length > 0 ) {
      this.utils.showModal(
        'Some permissions need another permission',
        '<p>Some ticked permissions have no effect in the Web Portal without another permission:</p>'
          + `<ul>${this.buildUnmetDependencySummary(unmet)}</ul>`
          + '<p>Do you want to save the role anyway? Select <b>No</b> to go back and use <b>Add missing permissions</b>.</p>',
        (): void => {
          this.saveRole(autoCloseDialog);
        }
      );
      return;
    }

    this.saveRole(autoCloseDialog);
  }

  /**
   * Saves the role. Called by onSubmit() once the form is valid and any dependency warning has been
   * accepted.
   *
   * @param autoCloseDialog An optional param to indicate if the dialog should be closed or not.
   * @returns Nothing.
   */
  private saveRole(autoCloseDialog?: boolean): void {
    if ( this.refusePartnerRoleSave() ) {
      return;
    }
    // Fetch and prepare the selected permissions. Only get permissions that the client checked.
    this.rbac_role.rbac_permissions = Object.values(this.grouped_rbac_permissions)
      .reduce((acc: RbacPermission[], group) => {
        const selectedPermissions: RbacPermission[] = group.rbac_permissions.filter((rbac_permission: RbacPermission): boolean => {
          return rbac_permission.checked;
        });
        return acc.concat(selectedPermissions);
      }, []);

    // Store the request Promise in this.
    let request: Promise<any>;
    let hasErrors: boolean = false;
    // Remember whether this save creates the role, before the id is assigned by the response.
    const isCreate: boolean = !this.rbac_role.id;

    if ( this.rbac_role.id ) {
      // Make an update request if the record id is present.
      request = this.api.makeRequest('put', `v2/account/rbac-roles/${this.rbac_role.id}`, this.rbac_role)
      .then((response: RbacRole): void => {
        this.rbac_role = response;
        this.utils.showToast(`The RBAC role was updated.`);
      })
      .catch((errorResponse: any): void => {
        hasErrors = true;
        this.utils.handleAPIErrors(errorResponse);
      });
    } else {
      // Make a "create" request when the record id is not present.
      request = this.api.makeRequest('post', `v2/account/rbac-roles`, this.rbac_role)
      .then((response: RbacRole): void => {
        this.rbac_role = response;
        // Emit the RBAC role id.
        this.rbac_role_id_changed.emit(this.rbac_role.id);
        // Show a success message.
        this.utils.showToast(`The RBAC role was created.`);
      })
      .catch((errorResponse: any): void => {
        hasErrors = true;
        this.utils.handleAPIErrors(errorResponse);
      });
    }

    // Check if the dialog should close. It should only close if there are no API errors.
    request.finally((): void => {
      // Terminate early if there are any errors.
      if ( hasErrors ) {
        return;
      }
      // A create-only user cannot update the role they just created, so close the dialog for them.
      const canEdit: boolean = this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES, RBACService.RBAC_A.A_EDIT)
      ], true);

      // Check if the dialog should be closed.
      if ( autoCloseDialog || (isCreate && !canEdit) ) {
        // Handle close dialog event emitter.
        this.close_dialog.emit(true);
      } else {
        // Remap the RBAC permissions.
        this.mapRBACPermissions();
      }
    });
  }

}
